import React, { useEffect, useRef, useState } from 'react';
import { getSession, signOut } from './api';
import Monitoring from './Monitoring';
import SignIn from './SignIn';
import './delta-force.css';

// Menu is data so a new tool is one line here plus a component.
const TOOLS = [
  { key: 'searching', label: 'searching', ready: false },
  { key: 'monitoring', label: 'monitoring', ready: true, view: Monitoring },
];

// Theme: remembered choice, else whatever the site is showing (App.dark).
const readTheme = () => { try { const t = localStorage.getItem('df:theme'); if (t === 'dark' || t === 'light') return t; } catch { /* private mode */ } return null; };

export default function DeltaForce({ onExit }) {
  const [user, setUser] = useState(() => getSession()?.user || null);
  const root = useRef(null);
  const [theme, setTheme_] = useState(readTheme);
  useEffect(() => {
    if (theme === null) setTheme_(root.current?.closest('.App')?.classList.contains('dark') ? 'dark' : 'light');
  }, [theme]);
  const setTheme = t => { setTheme_(t); try { localStorage.setItem('df:theme', t); } catch { /* private mode */ } };
  // Arriving back from a broker portal lands straight in monitoring.
  const [active, setActive] = useState(() => (new URLSearchParams(window.location.search).has('df') ? 'monitoring' : null));
  const [cursor, setCursor] = useState(TOOLS.findIndex(t => t.ready));

  useEffect(() => {
    const onKey = e => {
      if (e.target.tagName === 'INPUT') { if (e.key === 'Escape') e.target.blur(); return; }
      if (e.key === 'Escape') { active ? setActive(null) : onExit(); return; }
      if (active || !user) return;
      const n = Number(e.key);
      if (n >= 1 && n <= TOOLS.length && TOOLS[n - 1].ready) { setActive(TOOLS[n - 1].key); return; }
      if (e.key === 'ArrowDown' || e.key === 'j') setCursor(c => (c + 1) % TOOLS.length);
      if (e.key === 'ArrowUp' || e.key === 'k') setCursor(c => (c - 1 + TOOLS.length) % TOOLS.length);
      if (e.key === 'Enter' && TOOLS[cursor].ready) setActive(TOOLS[cursor].key);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, cursor, onExit, user]);

  const tool = TOOLS.find(t => t.key === active);
  const View = tool?.view;
  const back = () => (active ? setActive(null) : onExit());

  return (
    <div className={`df ${theme ? `df-${theme}` : ''}`} ref={root}>
      <header className="df-top">
        <h1>
          <button onClick={back}>delta force</button>
          {tool && <span> / {tool.label}</span>}
        </h1>
        <span className="df-actions">
          {user && <span className="df-muted">{user.username}</span>}
          <button className="df-esc" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            title={theme === 'dark' ? 'switch to light' : 'switch to dark'} aria-label="toggle theme">{theme === 'dark' ? 'light' : 'dark'}</button>
          {user && <button className="df-esc" onClick={async () => { await signOut(); setUser(null); setActive(null); }}>sign out</button>}
          <button className="df-esc" onClick={back}>esc</button>
        </span>
      </header>

      {!user ? (
        <SignIn onDone={setUser} />
      ) : View ? (
        <View />
      ) : (
        <ol className="df-menu">
          {TOOLS.map((t, i) => (
            <li key={t.key} className={`${t.ready ? '' : 'off'} ${i === cursor ? 'cur' : ''}`}>
              <button disabled={!t.ready} onClick={() => setActive(t.key)} onMouseEnter={() => setCursor(i)}>
                <span className="df-idx">{String(i + 1).padStart(2, '0')}</span>
                <span>{t.label}</span>
                <span className="df-muted">{t.ready ? '→' : 'soon'}</span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
